import React, { useState, useEffect, useMemo } from "react";
import "./Separation.css";
import "../client/Client.css"; // same table styles as Employee module
import config from "../../config";
import Header from "../../components/common/Header";
import { IoSearchOutline } from "react-icons/io5";
import SeparationDetail from "./SeparationDetail";
import AddEmployee from "../employee/AddEmployee";

const Separation = ({ setActivePage }) => {
  const [activeTab, setActiveTab] = useState("employee"); // "employee" | "separated"
  const [employees, setEmployees] = useState([]);
  const [separatedEmployees, setSeparatedEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  // Employee list tab → AddEmployee with fromSeparation=true (no Edit/Delete, has Assets+Terminate+Exit)
  // Separated tab → SeparationDetail
  const [selectedEmployee, setSelectedEmployee] = useState(null);   // for separated tab
  const [showAddEmployee, setShowAddEmployee] = useState(false);     // for employee tab
  const [selectedEmpData, setSelectedEmpData] = useState(null);     // full detail for AddEmployee

  const itemsPerPage = 8;
  const [userDetails, setUserDetails] = useState({ token: "", orgId: "" });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({ token, orgId });
  }, []);

  // Employee list tab — same API as Employee module (no status filter)
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    if (activeTab !== "employee") return;
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, activeTab, currentPage, searchTerm]);

  // Separated tab — filter status=InActive
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    if (activeTab !== "separated") return;
    fetchSeparatedEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails, activeTab, currentPage, searchTerm]);

  // Same exact API as Employee.js — no status param
  const fetchEmployees = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({
        orgKey: userDetails.orgId,
        sort: "id,desc",
        page: currentPage,
        size: itemsPerPage,
      });
      if (searchTerm) params.append("search", searchTerm);

      const res = await fetch(
        `${config.API_BASE_URL}${config.GET_VIEW_EMPLOYEES_ENDPOINT}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch employees");
      }
      
      let rawContent = data.content || data.body || [];
      let content = Array.isArray(rawContent) 
        ? rawContent 
        : (Array.isArray(rawContent.content) ? rawContent.content : (Array.isArray(rawContent.employees) ? rawContent.employees : []));

      const normalized = content.map((emp) => ({
        employeeId: emp.employeeId,
        name: emp.employeeName,
        clientName: emp.clientName || "-",
        key: emp.employeeCode,
        site: emp.workSite || "—",
        role: emp.jobRole || "—",
        status: emp.status || "Active",
      }));
      setEmployees(normalized);
      const totalCount = data.totalElements || (data.body && data.body.totalEmployees) || normalized.length;
      setTotalElements(totalCount);
      setTotalPages(data.totalPages || (data.body && data.body.totalPages) || Math.ceil(totalCount / itemsPerPage));
    } catch (err) {
      console.error("Fetch employees error:", err);
      setErrorMsg(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const fetchSeparatedEmployees = async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = new URLSearchParams({
        orgKey: userDetails.orgId,
        sort: "id,desc",
        page: currentPage,
        size: itemsPerPage,
        status: "InActive",
      });
      if (searchTerm) params.append("search", searchTerm);

      const res = await fetch(
        `${config.API_BASE_URL}${config.GET_VIEW_EMPLOYEES_ENDPOINT}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to fetch separated employees");
      }

      let rawContent = data.content || data.body || [];
      let content = Array.isArray(rawContent) 
        ? rawContent 
        : (Array.isArray(rawContent.content) ? rawContent.content : (Array.isArray(rawContent.employees) ? rawContent.employees : []));

      const normalized = content.map((emp) => ({
        employeeId: emp.employeeId,
        name: emp.employeeName,
        clientName: emp.clientName || "-",
        key: emp.employeeCode,
        site: emp.workSite || "—",
        role: emp.jobRole || "—",
        status: emp.status || "InActive",
        terminateExitStatus: emp.terminateExitStatus || "",
      }));
      setSeparatedEmployees(normalized);
      const totalCount = data.totalElements || (data.body && data.body.totalEmployees) || normalized.length;
      setTotalElements(totalCount);
      setTotalPages(data.totalPages || (data.body && data.body.totalPages) || Math.ceil(totalCount / itemsPerPage));
    } catch (err) {
      console.error("Fetch separated employees error:", err);
      setErrorMsg(err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  // Employee list tab click → fetch full detail → open AddEmployee with fromSeparation=true
  // (hides Edit/Delete, shows Assets + Terminate + Exit)
  const handleEmployeeListClick = async (emp) => {
    try {
      const token = localStorage.getItem("Authorization");
      const res = await fetch(
        `${config.API_BASE_URL}${config.GET_EMPLOYEE_DETAIL_ENDPOINT}${emp.employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );
      if (res.ok) {
        const data = await res.json();
        const empData = data.body || data.data || data;
        setSelectedEmpData(empData);
        setShowAddEmployee(true);
      }
    } catch (err) {
      console.error("Failed to fetch employee details:", err);
    }
  };

  // Separated tab click → open SeparationDetail
  const handleSeparatedClick = (emp) => {
    setSelectedEmployee(emp);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setSearchTerm("");
    setSelectedEmployee(null);
    setShowAddEmployee(false);
    setSelectedEmpData(null);
  };

  const handleBackToList = () => {
    setSelectedEmployee(null);
    setShowAddEmployee(false);
    setSelectedEmpData(null);
    if (activeTab === "employee") fetchEmployees();
    else fetchSeparatedEmployees();
  };

  const filteredEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) => {
      return (
        (emp.name || "").toLowerCase().includes(q) ||
        (emp.clientName || "").toLowerCase().includes(q) ||
        (emp.key || "").toLowerCase().includes(q) ||
        (emp.site || "").toLowerCase().includes(q) ||
        (emp.role || "").toLowerCase().includes(q) ||
        (emp.status || "").toLowerCase().includes(q)
      );
    });
  }, [employees, searchTerm]);

  const filteredSeparatedEmployees = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return separatedEmployees;
    return separatedEmployees.filter((emp) => {
      return (
        (emp.name || "").toLowerCase().includes(q) ||
        (emp.clientName || "").toLowerCase().includes(q) ||
        (emp.key || "").toLowerCase().includes(q) ||
        (emp.site || "").toLowerCase().includes(q) ||
        (emp.role || "").toLowerCase().includes(q) ||
        (emp.status || "").toLowerCase().includes(q) ||
        (emp.terminateExitStatus || "").toLowerCase().includes(q)
      );
    });
  }, [separatedEmployees, searchTerm]);

  const indexOfFirstItem = currentPage * itemsPerPage;
  const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalElements);
  const displayList = activeTab === "employee" ? filteredEmployees : filteredSeparatedEmployees;

  // Employee list tab → AddEmployee with fromSeparation (no Edit/Delete, Assets+Terminate+Exit)
  if (showAddEmployee && selectedEmpData) {
    return (
      <AddEmployee
        onCancel={handleBackToList}
        onSuccess={handleBackToList}
        selectedEmployee={selectedEmpData}
        fetchEmployees={fetchEmployees}
        fromSeparation={true}
      />
    );
  }

  // Separated tab → SeparationDetail
  if (selectedEmployee) {
    return (
      <SeparationDetail
        employee={selectedEmployee}
        onBack={handleBackToList}
        setActivePage={setActivePage}
      />
    );
  }

  // List view
  return (
    <div>
      <Header
        title="Separation"
        subtitle="Separation Information"
        onProfileClick={() => setActivePage && setActivePage("Profile")}
      />

      <div className="table-container">
        {/* Single row: search left | tabs center | spacer right */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>

          {/* Left: Search */}
          <div style={{ position: "relative", display: "flex", alignItems: "center", flex: 1 }}>
            <IoSearchOutline
              style={{ position: "absolute", left: "15px", fontSize: "15px", color: "#999" }}
            />
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
              style={{ paddingLeft: "40px", width: "145px", height: "15px" }}
            />
          </div>

          {/* Center: Tab buttons */}
          <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
            <div className="sep-tabs">
              <button
                className={`sep-tab-btn ${activeTab === "employee" ? "active" : ""}`}
                onClick={() => handleTabChange("employee")}
              >
                Employee list
              </button>
              <button
                className={`sep-tab-btn ${activeTab === "separated" ? "active" : ""}`}
                onClick={() => handleTabChange("separated")}
              >
                Separated employee list
              </button>
            </div>
          </div>

          {/* Right: spacer to balance layout */}
          <div style={{ flex: 1 }} />
        </div>

        {/* Table — identical class names to Employee module */}
        <table className="table">
          <thead>
            <tr>
              <th className="th">Employee Name</th>
              <th className="th">Client Name</th>
              <th className="th">Employee Key</th>
              <th className="th">Work Site</th>
              <th className="th">Job Role</th>
              <th className="th">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="td" colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                  Loading employee data...
                </td>
              </tr>
            ) : errorMsg ? (
              <tr>
                <td className="td" colSpan={6} style={{ textAlign: "center", color: "red", padding: "20px" }}>
                  {errorMsg}
                </td>
              </tr>
            ) : displayList.length === 0 ? (
              <tr>
                <td className="td" colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                  No employees found
                </td>
              </tr>
            ) : (
              displayList.map((emp) => (
                <tr
                  key={emp.employeeId}
                  onClick={() => {
                    handleEmployeeListClick(emp);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td className="td">{emp.name}</td>
                  <td className="td">{emp.clientName}</td>
                  <td className="td">{emp.key}</td>
                  <td className="td">{emp.site}</td>
                  <td className="td">{emp.role}</td>
                  <td className="td">
                    <span
                      className={`status-badge ${
                        activeTab === "separated" && emp.terminateExitStatus
                          ? emp.terminateExitStatus === "Terminated"
                            ? "terminated"
                            : "exit"
                          : emp.status?.toLowerCase() === "active"
                          ? "active"
                          : "inactive"
                      }`}
                    >
                      {activeTab === "separated" && emp.terminateExitStatus
                        ? emp.terminateExitStatus
                        : emp.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer */}
        <div
          className="table-footer"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", padding: "15px 0" }}
        >
          <div className="record-count" style={{ fontSize: "14px", color: "#666" }}>
            Showing {totalElements === 0 ? 0 : indexOfFirstItem + 1} to{" "}
            {indexOfLastItem} of {totalElements} entries
          </div>

          <div className="pagination" style={{ display: "flex", gap: "5px" }}>
            <button
              className="pagination-button"
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              style={{ padding: "8px 12px", border: "1px solid #ddd", backgroundColor: currentPage === 0 ? "#f5f5f5" : "#fff", cursor: currentPage === 0 ? "not-allowed" : "pointer", borderRadius: "4px", opacity: currentPage === 0 ? 0.5 : 1 }}
            >
              &lt;
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-button ${currentPage === i ? "active" : ""}`}
                onClick={() => setCurrentPage(i)}
                style={{ padding: "8px 12px", border: "1px solid #ddd", backgroundColor: currentPage === i ? "#232d5c" : "#fff", color: currentPage === i ? "#fff" : "#000", cursor: "pointer", borderRadius: "4px", minWidth: "40px" }}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-button"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setCurrentPage((p) => p + 1)}
              style={{ padding: "8px 12px", border: "1px solid #ddd", backgroundColor: currentPage >= totalPages - 1 ? "#f5f5f5" : "#fff", cursor: currentPage >= totalPages - 1 ? "not-allowed" : "pointer", borderRadius: "4px", opacity: currentPage >= totalPages - 1 ? 0.5 : 1 }}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Separation;
